import { Box, FormControl, FormHelperText, FormLabel, GridLegacy as Grid, Input, Chip } from '@mui/material'
import { ChipListComponent, ChipOptions } from 'components/core/chip-list'
import { sxc } from 'components/no-ui/styles/styleTypes'
import { List } from 'immutable'
import { ChangeEvent, ClipboardEvent, KeyboardEvent, useId, useState } from 'react'
import { ChipInputStyles } from './styles'

export type ChipInputProps<T extends React.ReactNode> = {
    value: T[]
    renderLabel: (value: T) => React.ReactNode
    setValue?: (value: T[]) => void
    makeValue: (input: string) => T

    disabled?: boolean
    editable?: boolean
    error?: boolean
    placeholder?: string
    label?: React.ReactNode
    helperText?: React.ReactNode
    'aria-label'?: string
}

export const ChipInput = <T extends React.ReactNode>({
    error,
    disabled,
    editable,
    label,
    value,
    setValue,
    renderLabel,
    makeValue,
    helperText,
    'aria-label': ariaLabel,
}: ChipInputProps<T>): React.ReactElement => {
    const inputId = useId()
    const [inputValue, setInputValue] = useState<string>('')

    const handleDelete = (item: T): void => {
        if (!setValue) {
            return
        }
        setValue(
            value.filter((v) => {
                return v !== item
            }),
        )
    }

    const handleInputChange = (ev: ChangeEvent<HTMLInputElement>): void => {
        setInputValue(ev.currentTarget.value)
    }
    const addItem = (item: string): void => {
        item = item.trim()
        if (item === '') {
            return
        }

        const v = makeValue(item)
        if (value.includes(v)) {
            setInputValue('')
            return
        }
        value.push(v)

        if (!setValue) {
            return
        }

        setValue(value)
        setInputValue('')
    }

    const handleKeyDown = (ev: KeyboardEvent<HTMLInputElement>): void => {
        if (ev.key === 'Enter' || ev.key === ',') {
            ev.preventDefault()
            addItem(inputValue)
            return
        }

        if (ev.key === 'Backspace') {
            if (ev.currentTarget.value === '') {
                ev.preventDefault()
                if (value.length > 0) {
                    value.pop()
                    if (!setValue) {
                        return
                    }
                    setValue(value)
                }
                return
            }
        }

        if (ev.key === 'Escape') {
            setInputValue('')
        }
    }

    const handlePaste = (ev: ClipboardEvent<HTMLInputElement>): void => {
        const text = ev.clipboardData.getData('text')
        if (!text) return

        // Check if paste contains separators (multi-value paste)
        if (/[,;\n\t]/.test(text)) {
            ev.preventDefault()
            const items = text.split(/[,;\n\t]+/).map((s) => s.trim()).filter(Boolean)
            items.forEach((item) => addItem(item))
        }
        // Single value paste: let default behavior handle it
    }

    return (
        <Grid item xs={12}>
            <Grid item>
                <FormLabel htmlFor={inputId}>{label}</FormLabel>
            </Grid>
            <Grid item>
                {!editable && (
                    <ChipListComponent
                        options={{ small: true, outlined: true }}
                        items={List(
                            value.map((v, index): ChipOptions => {
                                return {
                                    id: index.toString(),
                                    label: v,
                                }
                            }),
                        )}
                    />
                )}
                {editable && (
                    <FormControl fullWidth error={error}>
                        <Box sx={sxc(ChipInputStyles.chipsContainer, [error, ChipInputStyles.error])}>
                            {value.map((v: T, i: number): React.ReactElement => {
                                return (
                                    <Chip
                                        sx={ChipInputStyles.chip}
                                        key={`chip-${v}-${i}`}
                                        color="info"
                                        label={renderLabel(v)}
                                        onDelete={(): void => {
                                            handleDelete(v)
                                        }}
                                    />
                                )
                            })}
                            <Input
                                sx={sxc(ChipInputStyles.inputRoot, ChipInputStyles.input)}
                                disableUnderline
                                value={inputValue}
                                onKeyDown={handleKeyDown}
                                onPaste={handlePaste}
                                onChange={(ev: ChangeEvent<HTMLInputElement>): void => {
                                    handleInputChange(ev)
                                }}
                                onBlur={(): void => {
                                    addItem(inputValue)
                                }}
                                disabled={disabled}
                                id={inputId}
                                inputProps={{ 'aria-label': ariaLabel }}
                            />
                        </Box>
                        {helperText && (
                            <FormHelperText sx={sxc([error, ChipInputStyles.error])}>{helperText}</FormHelperText>
                        )}
                    </FormControl>
                )}
            </Grid>
        </Grid>
    )
}
